import { Info } from "@phosphor-icons/react";
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "./ui/tooltip";

/**
 * 7-band Narrative Strength scale, defined by the user. Each band carries
 * a label, what's happening culturally, and the strategic interpretation.
 */
export const STRENGTH_BANDS = [
    { min: 0,  max: 9,   label: "Noise",                 culture: "Almost no meaningful signal. Scattered mentions only.",                          strategy: "Ignore unless highly niche or early-research exploration.",        cls: "bg-muted text-muted-foreground", barCls: "bg-muted-foreground/40" },
    { min: 10, max: 24,  label: "Weak Signal",           culture: "Small pockets of conversation beginning to form.",                              strategy: "Interesting for trend scouts, too early for market confidence.",    cls: "bg-slate-200 text-slate-700", barCls: "bg-slate-400" },
    { min: 25, max: 39,  label: "Emerging Narrative",    culture: "Repeated language and themes appearing across communities.",                    strategy: "Early opportunity zone. Requires careful validation.",              cls: "bg-amber-100 text-amber-800", barCls: "bg-amber-500" },
    { min: 40, max: 54,  label: "Cultural Tension Zone", culture: "Real resonance exists, but contradictions and skepticism are strong.",          strategy: "High potential category-creation territory. Market is unsettled.",  cls: "bg-orange-100 text-orange-800", barCls: "bg-orange-500" },
    { min: 55, max: 69,  label: "Directional Shift",     culture: "Public sentiment beginning to consistently lean one direction.",                strategy: "Strong positioning opportunity. Momentum becoming durable.",        cls: "bg-rose-100 text-rose-800", barCls: "bg-rose-500" },
    { min: 70, max: 84,  label: "Cultural Adoption",     culture: "Narrative widely accepted and socially reinforced.",                            strategy: "Market becoming mainstream. Competition increases rapidly.",        cls: "bg-emerald-100 text-emerald-800", barCls: "bg-emerald-600" },
    { min: 85, max: 100, label: "Cultural Entrenchment", culture: "The narrative is now assumed or normalized.",                                   strategy: "Late-stage market. Harder to differentiate. Innovation shifts to optimization.", cls: "bg-foreground text-background", barCls: "bg-foreground" },
];

export function getStrengthBand(value) {
    const v = Math.max(0, Math.min(100, Math.round(value || 0)));
    return STRENGTH_BANDS.find((b) => v >= b.min && v <= b.max) || STRENGTH_BANDS[0];
}

/**
 * NarrativeStrengthMeter — number + colored bar + band label + ⓘ tooltip.
 * Shows the score band the value falls into, with the full 7-band reference
 * in the tooltip so users can read the score without a legend.
 */
export default function NarrativeStrengthMeter({ value, compact = false, showLabel = true }) {
    const v = Math.max(0, Math.min(100, Math.round(value || 0)));
    const band = getStrengthBand(v);

    return (
        <TooltipProvider delayDuration={150}>
            <div className="inline-flex items-center gap-2.5" data-testid="strength-meter">
                <div className="flex items-baseline gap-1.5">
                    <span className={`font-display font-bold tabular-nums leading-none ${compact ? "text-base" : "text-2xl"}`} data-testid="strength-value">{v}</span>
                    <span className="font-mono text-[10px] text-muted-foreground tracking-wider">/ 100</span>
                </div>
                <div className={`h-1.5 ${compact ? "w-20" : "w-28"} bg-muted rounded-full overflow-hidden`} data-testid="strength-bar">
                    <div className={`h-full ${band.barCls}`} style={{ width: `${v}%` }} />
                </div>
                {showLabel && (
                    <span
                        data-testid="strength-band-label"
                        className={`font-mono text-[10px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-sm ${band.cls}`}
                    >
                        {band.label}
                    </span>
                )}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            type="button"
                            data-testid="strength-info"
                            className="text-muted-foreground hover:text-primary"
                            aria-label="How narrative strength is interpreted"
                        >
                            <Info size={13} weight="duotone" />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent
                        side="bottom"
                        align="end"
                        className="max-w-md p-0 bg-card border border-border text-foreground shadow-xl"
                    >
                        <div className="p-4">
                            <div className="overline mb-1">Narrative Strength · 0–100 scale</div>
                            <div className="font-display font-semibold text-sm mb-3">Where this score lands: <span className="text-primary">{band.label}</span></div>
                            <p className="editorial text-[12px] text-foreground/85 leading-relaxed mb-3">
                                <span className="font-medium">Culturally: </span>{band.culture}
                            </p>
                            <p className="editorial text-[12px] text-foreground/85 leading-relaxed mb-4">
                                <span className="font-medium">Strategy: </span>{band.strategy}
                            </p>
                            <div className="border-t border-border pt-3">
                                <div className="overline text-[9px] mb-2">Full scale</div>
                                <ul className="space-y-1 text-[11px] font-mono">
                                    {STRENGTH_BANDS.map((b) => (
                                        <li key={b.label} className={`flex items-baseline gap-2 ${b.label === band.label ? "font-bold text-foreground" : "text-muted-foreground"}`}>
                                            <span className="tabular-nums w-12 shrink-0">{b.min}–{b.max}</span>
                                            <span>{b.label}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </TooltipContent>
                </Tooltip>
            </div>
        </TooltipProvider>
    );
}
